import { Button, Grid, LinearProgress, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../../components/cancelButton/CancelButton";
import ConfirmationDialog from "../../components/confirmationDialog/ConfirmationDialog";
import type { CreateOrUpdateRewardRequest, RewardResponse } from "../../rewards/rewardsApi";
import type { Reward } from "./RewardEdit";
import RewardEdit from "./RewardEdit";
import { useReward } from "./useReward";

interface Props {
  readonly existingReward: RewardResponse;
  readonly onUpdateReward: (request: CreateOrUpdateRewardRequest) => Promise<RewardResponse>;
  readonly onDeleteReward: () => Promise<void>;
}

const EditRewardPage: FC<Props> = ({ existingReward, onUpdateReward, onDeleteReward }) => {
  const navigate = useNavigate();
  const [confirmationDialogVisible, setConfirmationDialogVisible] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const { control, handleSubmit, watch } = useForm<Reward>({
    defaultValues: existingReward
      ? {
          ...existingReward,
          name: existingReward.name,
          description: existingReward.description,
          cost: existingReward.cost,
          image: existingReward.image,
        }
      : undefined,
  });

  useEffect(() => {
    const subscription = watch(() => setErrors((s) => (s.length === 0 ? s : [])));
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleDelete = async () => {
    await onDeleteReward();
    navigate("/admin/rewards");
  };

  const handleDuplicate = () => {
    navigate(`/admin/rewards/new?from=${existingReward.id}`);
  };

  const submitReward = async (state: Reward) => {
    await onUpdateReward({
      name: state.name,
      description: state.description,
      cost: state.cost,
      image: state.image,
    });
  };

  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 1,
        marginBottom: 1,
        paddingLeft: 1,
        paddingRight: 1,
      }}
    >
      <Grid container size={12}>
        <RewardEdit control={control} errors={errors} />
        <Grid size={12} container>
          <Stack direction="row" spacing={2}>
            <CancelButton onClick={() => navigate("/admin/rewards")} />
            <Button variant="outlined" color="error" onClick={() => setConfirmationDialogVisible(true)}>
              Usuń
            </Button>
            <Button variant="outlined" color="secondary" onClick={handleDuplicate}>
              Duplikuj
            </Button>
            <AsyncLoadingButton onClick={handleSubmit(submitReward)}>Zapisz</AsyncLoadingButton>
            <ConfirmationDialog
              open={confirmationDialogVisible}
              title="Usuń nagrodę"
              content="Czy chcesz usunąć nagrodę? Tej operacji nie będzie można już cofnąć."
              onCancel={() => setConfirmationDialogVisible(false)}
              onConfirm={handleDelete}
            />
          </Stack>
        </Grid>
      </Grid>
    </Grid>
  );
};

const InitializedEditRewardPage = () => {
  const { reward, isLoading, updateReward, deleteReward } = useReward();

  if (isLoading) {
    return (
      <Grid container>
        <Grid size={12}>
          <LinearProgress data-testid="loading-indicator" />
        </Grid>
      </Grid>
    );
  }

  if (!reward) {
    return (
      <Grid
        container
        spacing={2}
        sx={{
          marginTop: 1,
          marginBottom: 1,
        }}
      >
        <Typography
          sx={{
            margin: 2,
          }}
        >
          Taka nagroda nie istnieje
        </Typography>
      </Grid>
    );
  }

  return (
    <EditRewardPage
      existingReward={reward}
      onUpdateReward={updateReward}
      onDeleteReward={() => deleteReward(reward.id)}
    />
  );
};

export default InitializedEditRewardPage;
